import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Pencil, Trash2, Handshake, Sparkles, AlertCircle, ExternalLink, Calculator, SpellCheck2, Ruler, BookMarked, BookmarkPlus } from "lucide-react";
import { api, BRL, shownUnit, reasonRequired } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import CalculatorDialog from "@/components/CalculatorDialog";
import MaterialEstimateDialog from "@/components/MaterialEstimateDialog";
import VoiceItemButton from "@/components/VoiceItemButton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SchoolCombobox from "@/components/SchoolCombobox";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";
import DeleteItemDialog from "@/components/DeleteItemDialog";
import FdpActivateDialog from "@/components/FdpActivateDialog";
import BudgetFdpOptions from "@/components/BudgetFdpOptions";
import { DiscountField, AdminNoteField, NotifyToggle } from "@/components/BudgetItemDialog";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import ShareBudgetDialog from "@/components/ShareBudgetDialog";
import ServiceCatalogDialog from "@/components/ServiceCatalogDialog";

const EMPTY_ITEM = { description: "", quantity: 1, unit_value: 0, discount: 0, observation: "", admin_note: "", without_value: false, before_photo_path: null, after_photo_path: null };

const VALIDITY_OPTIONS = [
  { value: 5, label: "5 dias" },
  { value: 15, label: "15 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "3 meses" },
];

const WARRANTY_OPTIONS = [
  "1 mês",
  "2 meses",
  "3 meses",
  "6 meses",
  "1 ano",
  "Sem garantia",
  "Apenas alguns itens têm garantia",
];

const SERVICE_TYPE_OPTIONS = [
  { value: "mao_de_obra", label: "Só mão de obra" },
  { value: "material_mao_de_obra", label: "Inclui material e mão de obra" },
  { value: "conforme_item", label: "De acordo com observação no item" },
];

const PAYMENT_OPTIONS = [
  { value: "apos_nf", label: "Após a entrega da nota fiscal" },
  { value: "entrada_30", label: "Precisa de entrada de 30%" },
  { value: "entrada_50", label: "Precisa de entrada de 50%" },
  { value: "manual", label: "Forma manual (digitar)" },
];

const DEFAULT_AWARENESS_TERM = `Ao aprovar este orçamento, o(a) diretor(a) e responsável pela escola declara ciência e concordância com as condições abaixo:

1. Os valores em HONORÁRIOS incluem material e mão de obra, bem como os impostos aproximados já embutidos no valor total.
2. A forma de pagamento indicada será respeitada após a entrega da nota fiscal.
3. O prazo de entrega é estimado em dias úteis e pode variar conforme condições do tempo, entrega de materiais e disponibilidade da equipe técnica.
4. A garantia cobre defeitos relacionados aos serviços executados dentro do período informado — não cobre uso inadequado, vandalismo ou intervenções de terceiros.
5. Após a aprovação, qualquer alteração ou inclusão de item deverá ser formalizada por novo orçamento ou aditivo.
6. A validade deste orçamento é limitada à data indicada; após este prazo, valores e condições podem ser revistos.`;

export default function BudgetForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [schools, setSchools] = useState([]);
  const [budget, setBudget] = useState(null);
  const [form, setForm] = useState({
    school_id: "",
    budget_date: new Date().toISOString().slice(0, 10),
    validity_days: 15,
    delivery_days: 7,
    warranty: "3 meses",
    service_type: "",
    payment_option: "apos_nf",
    payment_other: "",
    observation: "",
    admin_note: "",
    faz_depois_paga: false,
    fdp_log_mode: false,
    fiado: false,
    reference: "",
    awareness_term: DEFAULT_AWARENESS_TERM,
    down_payment_pct: 0,
  });
  const [pendingItems, setPendingItems] = useState([]);
  const [historyDialog, setHistoryDialog] = useState({ open: false, itemId: null });
  const [itemDialog, setItemDialog] = useState({ open: false, mode: "add", item: EMPTY_ITEM });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [spellLoading, setSpellLoading] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [saveToCatalogDialog, setSaveToCatalogDialog] = useState({ open: false, description: "", min_value: 0, max_value: 0, note: "" });
  // Modo de lançamentos FDP (orçamento começa vazio, serviços são lançados ao longo do período)
  const isNewBudget = !id;
  const [shareDialog, setShareDialog] = useState({ open: false, budget: null, school: null, title: "Enviar para a escola?", headlineLabel: "Mensagem que será enviada", extraMessage: "" });
  const [fdpDialog, setFdpDialog] = useState(false);

  const generateAiDescription = async () => {
    const prompt = itemDialog.item.description?.trim();
    if (!prompt) { toast.error("Digite uma descrição curta para a IA refinar"); return; }
    setAiLoading(true);
    try {
      const r = await api.post("/ai/generate-item", { prompt });
      setItemDialog((d) => ({ ...d, item: { ...d.item, description: r.data.text } }));
      toast.success("Descrição gerada pela IA");
    } catch (e) {
      toast.error("Falha ao gerar com IA");
    } finally {
      setAiLoading(false);
    }
  };

  const spellCorrectDescription = async () => {
    const prompt = itemDialog.item.description?.trim();
    if (!prompt) { toast.error("Digite algo primeiro"); return; }
    setSpellLoading(true);
    try {
      const r = await api.post("/ai/spell-correct", { prompt });
      setItemDialog((d) => ({ ...d, item: { ...d.item, description: r.data.text } }));
      toast.success("Português corrigido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao corrigir");
    } finally {
      setSpellLoading(false);
    }
  };

  const openSaveToCatalog = () => {
    const desc = itemDialog.item.description?.trim();
    const qty = Number(itemDialog.item.quantity || 1) || 1;
    const unit = Number(itemDialog.item.unit_value || 0);
    const total = unit * qty;
    if (!desc) { toast.error("Preencha a descrição"); return; }
    if (!unit || unit <= 0) { toast.error("Informe o valor unitário"); return; }
    // Pré-preenche min = max = total; usuário ajusta a faixa
    setSaveToCatalogDialog({
      open: true,
      description: desc,
      min_value: total,
      max_value: total,
      note: "",
    });
  };

  const confirmSaveToCatalog = async () => {
    const { description, min_value, max_value, note } = saveToCatalogDialog;
    const mn = Number(min_value || 0);
    const mx = Number(max_value || 0);
    if (!description.trim()) { toast.error("Preencha a descrição"); return; }
    if (!mn || mn <= 0) { toast.error("Informe o valor mínimo"); return; }
    if (!mx || mx <= 0) { toast.error("Informe o valor máximo"); return; }
    const selectedSchool = schools.find((s) => s.id === form.school_id);
    setCatalogSaving(true);
    try {
      await api.post("/services-catalog", {
        description: description.trim(),
        min_value: mn,
        max_value: mx,
        note: note || "",
        school_id: form.school_id || null,
        school_name: selectedSchool?.name || null,
        budget_id: id || null,
        budget_number: budget?.number || null,
      });
      toast.success("Salvo no catálogo com a faixa de valor");
      setSaveToCatalogDialog({ open: false, description: "", min_value: 0, max_value: 0, note: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar no catálogo");
    } finally {
      setCatalogSaving(false);
    }
  };

  const pickFromCatalog = (entry) => {
    if (!entry) return;
    // Usa o valor médio da faixa quando disponível; senão, o value único
    let total;
    if (entry.min_value != null && entry.max_value != null) {
      total = (Number(entry.min_value) + Number(entry.max_value)) / 2;
    } else {
      total = Number(entry.value || 0);
    }
    setItemDialog((d) => ({
      ...d,
      item: {
        ...d.item,
        description: entry.description || d.item.description,
        unit_value: Number((total / (Number(d.item.quantity || 1) || 1)).toFixed(2)),
      },
    }));
    toast.success("Item preenchido pelo catálogo");
  };

  useEffect(() => {
    (async () => {
      const r = await api.get("/schools", { params: { active: "true" } });
      setSchools(r.data);
      if (id) {
        const b = await api.get(`/budgets/${id}`);
        setBudget(b.data);
        setForm({
          school_id: b.data.school_id,
          budget_date: b.data.budget_date,
          validity_days: b.data.validity_days,
          delivery_days: b.data.delivery_days,
          warranty: b.data.warranty || "",
          service_type: b.data.service_type || "",
          payment_option: b.data.payment_option || "apos_nf",
          payment_other: b.data.payment_other || "",
          observation: b.data.observation || "",
          admin_note: b.data.admin_note || "",
          faz_depois_paga: !!b.data.faz_depois_paga,
          fdp_log_mode: !!b.data.fdp_log_mode,
          fiado: !!b.data.fiado,
          reference: b.data.reference || "",
          awareness_term: b.data.awareness_term || DEFAULT_AWARENESS_TERM,
          down_payment_pct: b.data.down_payment_pct || 0,
        });
      }
    })();
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveBudget = async () => {
    // Validation (all required; at least 1 item)
    const errors = [];
    const isComp = !!budget?.parent_budget_id;
    const isFdp = !!form.faz_depois_paga;
    const hideServiceType = isFdp && (isNewBudget || form.fdp_log_mode);
    if (!form.school_id) errors.push("Selecione uma escola");
    if (!form.budget_date) errors.push("Informe a data do orçamento");
    if (!isComp) {
      if (!isFdp && !form.validity_days) errors.push("Informe a validade");
      if (!isFdp && (form.delivery_days === "" || form.delivery_days == null)) errors.push("Informe os dias para entrega");
      if (!form.warranty) errors.push("Selecione a garantia");
      if (!hideServiceType && !form.service_type) errors.push("Selecione o tipo de serviço");
      if (!form.payment_option) errors.push("Selecione a forma de pagamento");
      if (form.payment_option === "manual" && !(form.payment_other || "").trim()) {
        errors.push("Descreva a forma de pagamento manual");
      }
      if (!(form.reference || "").trim()) errors.push("Informe a referência do orçamento");
      if (!(form.awareness_term || "").trim()) errors.push("Preencha o termo de ciência");
    }
    const items = id ? (budget?.items || []) : pendingItems;
    if (!items.length && !(isFdp && form.fdp_log_mode)) errors.push("Adicione pelo menos 1 item ao orçamento");
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    setSaving(true);
    try {
      const dp =
        form.payment_option === "entrada_30" ? 30 :
        form.payment_option === "entrada_50" ? 50 : 0;
      const payload = { ...form, down_payment_pct: dp };
      if (id) {
        await api.put(`/budgets/${id}`, payload);
        toast.success("Orçamento atualizado");
        nav(`/orcamentos/${id}`);
      } else {
        const r = await api.post("/budgets", payload);
        const newId = r.data.id;
        for (const it of pendingItems) {
          await api.post(`/budgets/${newId}/items`, {
            description: it.description,
            quantity: Number(it.quantity) || 1,
            unit_value: Number(it.unit_value) || 0,
            discount: it.without_value ? 0 : Number(it.discount) || 0,
            observation: it.observation || "",
            admin_note: it.admin_note || "",
            without_value: !!it.without_value,
            before_photo_path: it.before_photo_path || null,
            after_photo_path: it.after_photo_path || null,
          });
        }
        toast.success("Orçamento criado");
        try {
          const sr = await api.get(`/schools/${form.school_id}`);
          const fresh = await api.get(`/budgets/${newId}`);
          setShareDialog({ open: true, budget: fresh.data, school: sr.data });
        } catch {
          nav(`/orcamentos/${newId}/editar`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const openAddItem = () => setItemDialog({ open: true, mode: "add", item: { ...EMPTY_ITEM } });
  const openEditItem = (it) => setItemDialog({ open: true, mode: "edit", item: { ...it } });

  const [savingItem, setSavingItem] = useState(false);
  const savingItemRef = useRef(false);
  const saveItem = async () => {
    if (savingItemRef.current) return;
    const it = itemDialog.item;
    if (!it.description?.trim()) { toast.error("Descrição é obrigatória"); return; }
    const gross = (Number(it.quantity) || 1) * (Number(it.unit_value) || 0);
    const disc = it.without_value ? 0 : Number(it.discount) || 0;
    if (disc < 0 || disc > gross + 0.001) { toast.error("O desconto não pode ser maior que o valor do item"); return; }
    savingItemRef.current = true;
    setSavingItem(true);
    try {
      if (!id) {
        // Local pending items (budget not yet created)
        const localItem = {
          id: it.id || `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_value: Number(it.unit_value) || 0,
          discount: it.without_value ? 0 : Number(it.discount) || 0,
          total: (Number(it.quantity) || 1) * (Number(it.unit_value) || 0) - (it.without_value ? 0 : Number(it.discount) || 0),
          observation: it.observation || "",
          admin_note: it.admin_note || "",
          without_value: !!it.without_value,
          before_photo_path: it.before_photo_path || null,
          after_photo_path: it.after_photo_path || null,
          completed: false,
          paid: false,
        };
        if (itemDialog.mode === "edit") {
          setPendingItems((prev) => prev.map((p) => (p.id === it.id ? localItem : p)));
        } else {
          setPendingItems((prev) => [...prev, localItem]);
        }
        setItemDialog({ open: false, mode: "add", item: EMPTY_ITEM });
        toast.success("Item adicionado");
        return;
      }
      let r;
      const payload = {
        description: it.description,
        quantity: Number(it.quantity) || 1,
        unit_value: Number(it.unit_value) || 0,
        discount: it.without_value ? 0 : Math.round((Number(it.discount) || 0) * 100) / 100,
        observation: it.observation,
        admin_note: it.admin_note || "",
        without_value: !!it.without_value,
        before_photo_path: it.before_photo_path || null,
        after_photo_path: it.after_photo_path || null,
      };
      if (itemDialog.mode === "edit") {
        // Detect real change vs original item
        const original = (budget?.items || []).find((p) => p.id === it.id) || {};
        const changedFields = ["description", "quantity", "unit_value", "discount", "observation", "without_value"];
        const hasChange = changedFields.some((k) => {
          const a = payload[k], b = original[k];
          if (typeof a === "number" || typeof b === "number") return Number(a || 0) !== Number(b || 0);
          return (a ?? "") !== (b ?? "");
        });
        if (hasChange && reasonRequired(budget?.status) && !(it.edit_reason || "").trim()) {
          toast.error("Informe o motivo da alteração do item.");
          return;
        }
        if (it.edit_reason) payload.edit_reason = it.edit_reason;
        r = await api.put(`/budgets/${id}/items/${it.id}`, { ...payload, notify_school: it.notify_school !== false });
      } else {
        r = await api.post(`/budgets/${id}/items`, payload);
      }
      setBudget(r.data);
      setItemDialog({ open: false, mode: "add", item: EMPTY_ITEM });
      if (itemDialog.mode === "edit") {
        toast.success(`Orçamento Nº ${r.data.number} — item alterado`);
      } else {
        toast.success(`Orçamento Nº ${r.data.number} atualizado — item adicionado`);
      }
      // No WhatsApp modal on item add/edit — school is notified in background
      // via push + in-app notification. Status change / finalize flows keep the dialog.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar item");
    } finally {
      savingItemRef.current = false;
      setSavingItem(false);
    }
  };

  const uploadItemPhoto = async (which, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItemDialog((d) => ({ ...d, item: { ...d.item, [which]: r.data.path } }));
      toast.success(`Foto ${which === "before_photo_path" ? "antes" : "depois"} enviada`);
    } catch (e) {
      toast.error("Erro ao enviar foto");
    }
  };

  const [deletingItem, setDeletingItem] = useState(null); // item being deleted — opens dialog

  const requestDeleteItem = (item) => setDeletingItem(item);

  const deleteItem = async (itemId, reason) => {
    try {
      if (!id) {
        setPendingItems((prev) => prev.filter((p) => p.id !== itemId));
        toast.success("Item removido");
        setDeletingItem(null);
        return;
      }
      const r = await api.delete(
        `/budgets/${id}/items/${itemId}?reason=${encodeURIComponent(reason || "")}`,
      );
      setBudget(r.data);
      toast.success(`Item removido do orçamento Nº ${r.data.number}`);
      setDeletingItem(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir item");
    }
  };

  const deleteBudget = async () => {
    if (!window.confirm("Marcar este orçamento como DELETADO?\n\nEle fica no sistema no status 'Deletado' e pode ser recuperado depois.")) return;
    try {
      await api.delete(`/budgets/${id}`);
      toast.success("Orçamento marcado como deletado");
      nav("/orcamentos");
    } catch (e) { toast.error("Erro"); }
  };

  const hardDeleteBudget = async () => {
    const num = budget?.number || "?";
    const typed = window.prompt(
      `⚠️ APAGAR DEFINITIVAMENTE o orçamento Nº ${num}?\n\n` +
      `Esta ação é IRREVERSÍVEL — o orçamento, itens, histórico, concorrentes e observações serão apagados do banco.\n\n` +
      `Para confirmar, digite APAGAR em maiúsculas:`
    );
    if (typed !== "APAGAR") {
      if (typed !== null) toast.error("Confirmação incorreta — nada foi apagado.");
      return;
    }
    try {
      await api.delete(`/budgets/${id}/hard`);
      toast.success(`Orçamento Nº ${num} apagado definitivamente`);
      nav("/orcamentos");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao apagar"); }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav(id ? `/orcamentos/${id}` : "/orcamentos")} data-testid="btn-voltar-budget">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {id ? `Editar Orçamento Nº ${budget?.number || ""}` : "Novo orçamento"}
          </h1>
          <p className="text-sm text-slate-600">Preencha os dados e adicione os itens</p>
        </div>
        {form.faz_depois_paga && (
          <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold">
            <Handshake className="w-4 h-4" />
            FAZ DEPOIS PAGA
          </span>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 card-shadow p-6 space-y-6">
        <h3 className="font-heading text-sm font-semibold text-slate-900 border-b border-slate-100 pb-2">Dados do orçamento</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Escola *</Label>
            <SchoolCombobox
              schools={schools}
              value={form.school_id}
              onChange={(v) => update("school_id", v)}
              disabled={!!id}
              placeholder="Digite para buscar a escola..."
            />
          </div>
          <Field label="Nº do orçamento">
            <Input value={budget?.number || "(automático)"} disabled data-testid="input-number" />
          </Field>
          <div className="md:col-span-3" data-testid="fdp-options-block">
            <BudgetFdpOptions
              form={form}
              update={update}
              school={schools.find((x) => x.id === form.school_id) || null}
              budgetId={id || null}
              onActivate={() => setFdpDialog(true)}
            />
          </div>
          <div className="md:col-span-3">
            <Field label="Referência do orçamento *">
              <Input
                value={form.reference}
                onChange={(e) => update("reference", e.target.value)}
                placeholder="Ex.: Pintura externa — fachada principal"
                data-testid="input-reference"
              />
            </Field>
          </div>
          <Field label="Data do orçamento *">
            <Input type="date" value={form.budget_date} onChange={(e) => update("budget_date", e.target.value)} data-testid="input-budget-date" />
          </Field>
          {!form.faz_depois_paga && (
          <Field label="Validade">
            <Select value={String(form.validity_days)} onValueChange={(v) => update("validity_days", Number(v))}>
              <SelectTrigger data-testid="select-validity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VALIDITY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          )}
          {!form.faz_depois_paga && (
          <Field label="Dias para entrega">
            <Input type="number" min="0" value={form.delivery_days} onChange={(e) => update("delivery_days", Number(e.target.value))} data-testid="input-delivery" />
          </Field>
          )}
          <Field label="Tempo de garantia">
            <Select value={form.warranty} onValueChange={(v) => update("warranty", v)}>
              <SelectTrigger data-testid="select-warranty"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {WARRANTY_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {!(form.faz_depois_paga && (isNewBudget || form.fdp_log_mode)) && (
          <Field label="Tipo de serviço">
            <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
              <SelectTrigger data-testid="select-service-type"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          )}
          <Field label="Forma de pagamento">
            <Select value={form.payment_option} onValueChange={(v) => update("payment_option", v)}>
              <SelectTrigger data-testid="select-payment"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {PAYMENT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {form.payment_option === "manual" && (
            <div className="md:col-span-2">
              <Field label="Descreva a forma de pagamento">
                <Input
                  value={form.payment_other}
                  onChange={(e) => update("payment_other", e.target.value)}
                  placeholder="Ex.: parcelado em 3x, cheque, etc."
                  data-testid="input-payment-other"
                />
              </Field>
            </div>
          )}
          {(form.payment_option === "entrada_30" || form.payment_option === "entrada_50") && budget?.total > 0 && (
            <div className="md:col-span-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="down-payment-hint">
                Entrada ({form.payment_option === "entrada_30" ? "30%" : "50%"}): <b>{BRL(budget.total * (form.payment_option === "entrada_30" ? 0.3 : 0.5))}</b>
                {" "}· Saldo após nota fiscal: <b>{BRL(budget.total * (form.payment_option === "entrada_30" ? 0.7 : 0.5))}</b>
              </div>
            </div>
          )}

          <div className="md:col-span-3">
            <Field label="Observação geral">
              <Textarea value={form.observation} onChange={(e) => update("observation", e.target.value)} rows={3} data-testid="input-observation" />
            </Field>
          </div>
          <div className="md:col-span-3">
            <AdminNoteField value={form.admin_note} onChange={(v) => update("admin_note", v)} testid="input-budget-admin-note" label="Observação interna do orçamento — só você vê" />
          </div>
          <div className="md:col-span-3">
            <Field label="Termo de ciência (pré-preenchido — você pode editar)">
              <Textarea
                value={form.awareness_term}
                onChange={(e) => update("awareness_term", e.target.value)}
                rows={8}
                data-testid="input-awareness-term"
                className="font-mono text-xs"
              />
            </Field>
          </div>

          {/* Fiado checkbox (visible always; independent from FDP) */}
          <div className="md:col-span-3">
            <div className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${form.fiado ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
              <Checkbox
                checked={form.fiado}
                onCheckedChange={(v) => update("fiado", !!v)}
                data-testid="check-fiado"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">
                  Marcar como orçamento fiado (pagar depois)
                </p>
                <p className="text-xs text-slate-500">
                  Aparece no painel da escola em "Serviços em aberto" até ser pago (NF faturada).
                </p>
              </div>
              <Handshake className={`w-5 h-5 shrink-0 ${form.fiado ? "text-amber-600" : "text-slate-400"}`} />
            </div>
          </div>
        </div>
      </div>

      {(() => {
        const items = id ? (budget?.items || []) : pendingItems;
        const total = items.reduce((s, it) => s + (it.total ?? ((it.quantity || 1) * (it.unit_value || 0))), 0);
        return (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-slate-900">Itens do orçamento *</h3>
            <Button onClick={openAddItem} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="btn-add-item">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar item
            </Button>
          </div>

          {items.length === 0 ? (
            <p className="text-center py-6 text-slate-500 text-sm">{form.faz_depois_paga && form.fdp_log_mode ? "Modo lançamentos: não precisa de item — os serviços serão lançados na tela do orçamento" : "Nenhum item adicionado — adicione pelo menos 1 para salvar"}</p>
          ) : (
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div
                  key={it.id}
                  data-testid={`row-item-${idx}`}
                  className="rounded-xl border border-slate-200 bg-white p-3"
                >
                  {/* Linha 1: # + descrição em largura total */}
                  <div className="flex items-start gap-2">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 break-words whitespace-pre-wrap">
                        {it.description}
                      </p>
                      {it.observation && (
                        <p className="text-xs text-slate-500 mt-0.5 break-words whitespace-pre-wrap">
                          {it.observation}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {it.completed && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">
                            Concluído
                          </span>
                        )}
                        {it.paid && (
                          <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                            Pago
                          </span>
                        )}
                        {it.without_value && (
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full font-semibold">
                            Sem valor
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Linha 2: Qtd × Unit = Total (sem brigar com descrição) */}
                  {!it.without_value && (it.quantity || it.unit_value) ? (
                    <p className="text-xs text-slate-600 mt-2 pl-8">
                      Qtd {it.quantity ?? 1} × {BRL(shownUnit(it))} ={" "}
                      <strong className="text-slate-900">
                        {BRL(it.total ?? (it.quantity || 0) * (it.unit_value || 0))}
                      </strong>
                    </p>
                  ) : null}
                  {/* Linha 3: ações compactas */}
                  <div className="flex items-center gap-2 mt-2 pl-8">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openEditItem(it)}
                      data-testid={`btn-edit-item-${idx}`}
                      className="h-8 text-xs"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1 text-[#7c3aed]" />
                      Editar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => requestDeleteItem(it)}
                      data-testid={`btn-delete-item-${idx}`}
                      className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Excluir
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-3 border-t-2 border-slate-200 mt-3">
                <span className="text-sm font-semibold text-slate-600">Total:</span>
                <span className="text-lg font-bold text-[#1e3a8a]" data-testid="budget-total">
                  {BRL(total)}
                </span>
              </div>
            </div>
          )}
        </div>
        );
      })()}

      <div className="sticky bottom-4 bg-white/95 backdrop-blur p-3 pr-16 sm:pr-3 rounded-xl border border-slate-200 shadow-lg">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-3">
          {/* Danger group — stacks vertically on mobile so long labels never collide */}
          {id && (
            <div className="order-2 sm:order-1 grid grid-cols-1 gap-2 sm:flex sm:gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={deleteBudget}
                className="h-9 text-xs border-orange-200 text-orange-700 hover:bg-orange-50 w-full sm:w-auto"
                data-testid="btn-delete-budget"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Marcar como deletado
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={hardDeleteBudget}
                className="h-9 text-xs border-red-300 text-red-700 hover:bg-red-50 w-full sm:w-auto"
                data-testid="btn-hard-delete-budget"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Apagar definitivamente
              </Button>
            </div>
          )}
          {/* Right action group — always visible together */}
          <div className="order-1 sm:order-2 flex gap-2 justify-center sm:justify-end">
            <Button variant="outline" onClick={() => nav("/orcamentos")} data-testid="btn-cancel-budget" className="flex-1 sm:flex-none">
              Cancelar
            </Button>
            <Button onClick={saveBudget} disabled={saving} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white flex-1 sm:flex-none" data-testid="btn-save-budget">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </div>
      </div>

      <FdpActivateDialog
        open={fdpDialog}
        onOpenChange={setFdpDialog}
        school={schools.find((s) => s.id === form.school_id) || null}
        onSaved={(updated) => {
          setSchools((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
          // Also auto-mark budget as FDP now that it's available
          update("faz_depois_paga", true);
        }}
      />

      <CalculatorDialog
        open={calcOpen}
        onOpenChange={setCalcOpen}
        onUseAsValue={(v) => setItemDialog((d) => ({ ...d, item: { ...d.item, unit_value: v } }))}
      />

      <MaterialEstimateDialog
        open={estimateOpen}
        onOpenChange={setEstimateOpen}
        onUseDescription={(txt) => setItemDialog((d) => ({ ...d, item: { ...d.item, description: txt } }))}
        onUseObservation={(txt) => setItemDialog((d) => {
          const base = (d.item.observation || "").trim();
          return { ...d, item: { ...d.item, observation: base ? `${base}\n\n${txt}` : txt } };
        })}
      />

      <ItemHistoryDialog
        open={historyDialog.open}
        onOpenChange={(o) => setHistoryDialog((d) => ({ ...d, open: o }))}
        budgetId={id}
        itemId={historyDialog.itemId}
      />

      <DeleteItemDialog
        open={!!deletingItem}
        onOpenChange={(o) => { if (!o) setDeletingItem(null); }}
        item={deletingItem}
        onConfirm={(reason) => deleteItem(deletingItem?.id, reason)}
        reasonRequired={reasonRequired(budget?.status)}
      />

      <Dialog open={itemDialog.open} onOpenChange={(o) => setItemDialog((d) => ({ ...d, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{itemDialog.mode === "add" ? "Adicionar item" : "Editar item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Descrição *">
              <div className="flex gap-2 items-start">
                <AutoGrowTextarea
                  value={itemDialog.item.description}
                  onChange={(e) => setItemDialog((d) => ({ ...d, item: { ...d.item, description: e.target.value } }))}
                  minRows={1}
                  maxRows={10}
                  placeholder="Ex.: Pintura das salas de aula, troca de 6 lâmpadas LED..."
                  data-testid="input-item-description"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={generateAiDescription}
                  disabled={aiLoading || spellLoading}
                  className="shrink-0 border-[#7c3aed] text-[#7c3aed] hover:bg-[#7c3aed] hover:text-white"
                  data-testid="btn-ai-generate"
                  title="Melhorar descrição com IA"
                >
                  <Sparkles className="w-4 h-4" />
                  {aiLoading ? "..." : "IA"}
                </Button>
              </div>
              {/* Quick helpers row — voice + spell-correct + estimate + calculator */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <VoiceItemButton
                  disabled={aiLoading || spellLoading}
                  onParsed={(data) => {
                    setItemDialog((d) => ({
                      ...d,
                      item: {
                        ...d.item,
                        description: data.description || d.item.description,
                        quantity: data.quantity != null ? data.quantity : d.item.quantity,
                        unit_value: data.unit_value != null ? data.unit_value : d.item.unit_value,
                      },
                    }));
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={spellCorrectDescription}
                  disabled={spellLoading || aiLoading}
                  className="h-8 text-[11px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  data-testid="btn-spell-correct"
                  title="Corrigir ortografia / pontuação em português"
                >
                  <SpellCheck2 className="w-3 h-3 mr-1" /> {spellLoading ? "Corrigindo..." : "Corrigir português"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEstimateOpen(true)}
                  className="h-8 text-[11px] border-violet-200 text-violet-700 hover:bg-violet-50"
                  data-testid="btn-ai-estimate"
                  title="Estimar materiais e valores com IA"
                >
                  <Ruler className="w-3 h-3 mr-1" /> Materiais & valores (IA)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCalcOpen(true)}
                  className="h-8 text-[11px] border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="btn-open-calculator"
                  title="Abrir calculadora rápida"
                >
                  <Calculator className="w-3 h-3 mr-1" /> Calculadora
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCatalogOpen(true)}
                  className="h-8 text-[11px] border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  data-testid="btn-open-catalog"
                  title="Buscar valor base no catálogo histórico"
                >
                  <BookMarked className="w-3 h-3 mr-1" /> Catálogo
                </Button>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quantidade">
                <Input type="number" min="0" step="0.01" value={itemDialog.item.quantity || ""} placeholder="1" onChange={(e) => setItemDialog((d) => ({ ...d, item: { ...d.item, quantity: e.target.value } }))} data-testid="input-item-qty" />
              </Field>
              <Field label="Valor unitário (R$)">
                <MoneyInput
                  value={itemDialog.item.unit_value}
                  disabled={itemDialog.item.without_value}
                  onChange={(v) => setItemDialog((d) => ({ ...d, item: { ...d.item, unit_value: v } }))}
                  data-testid="input-item-unit"
                />
              </Field>
            </div>
            <Field label="Observação">
              <Textarea value={itemDialog.item.observation} onChange={(e) => setItemDialog((d) => ({ ...d, item: { ...d.item, observation: e.target.value } }))} rows={2} data-testid="input-item-obs" />
            </Field>
            {!itemDialog.item.without_value && <DiscountField item={itemDialog.item} onChange={(v) => setItemDialog((d) => ({ ...d, item: { ...d.item, discount: v } }))} />}
            <AdminNoteField value={itemDialog.item.admin_note} onChange={(v) => setItemDialog((d) => ({ ...d, item: { ...d.item, admin_note: v } }))} />
            <div className="flex items-center gap-2">
              <Checkbox checked={itemDialog.item.without_value} onCheckedChange={(v) => setItemDialog((d) => ({ ...d, item: { ...d.item, without_value: !!v } }))} data-testid="check-item-no-value" />
              <span className="text-sm text-slate-700">Item sem valor</span>
            </div>

            {/* Photos: antes/depois (optional) */}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
              <ItemPhotoField
                label="Foto ANTES"
                path={itemDialog.item.before_photo_path}
                onUpload={(f) => uploadItemPhoto("before_photo_path", f)}
                onRemove={() => setItemDialog((d) => ({ ...d, item: { ...d.item, before_photo_path: null } }))}
                testid="item-photo-before"
              />
              <ItemPhotoField
                label="Foto DEPOIS"
                path={itemDialog.item.after_photo_path}
                onUpload={(f) => uploadItemPhoto("after_photo_path", f)}
                onRemove={() => setItemDialog((d) => ({ ...d, item: { ...d.item, after_photo_path: null } }))}
                testid="item-photo-after"
              />
            </div>
            {itemDialog.mode === "edit" && (
              <>
                {id && (
                  <Field label={reasonRequired(budget?.status) ? "Motivo da alteração *" : "Motivo da alteração (opcional antes da aprovação)"}>
                    <Textarea
                      value={itemDialog.item.edit_reason || ""}
                      onChange={(e) => setItemDialog((d) => ({ ...d, item: { ...d.item, edit_reason: e.target.value } }))}
                      rows={2}
                      placeholder="Obrigatório quando qualquer dado do item for alterado — ex.: valor ajustado após nova medição..."
                      data-testid="input-item-edit-reason"
                    />
                  </Field>
                )}
                {id && <NotifyToggle isEdit checked={itemDialog.item.notify_school !== false} onChange={(v) => setItemDialog((d) => ({ ...d, item: { ...d.item, notify_school: v } }))} />}
                {id && itemDialog.item.versions?.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setHistoryDialog({ open: true, itemId: itemDialog.item.id })}
                    className="text-xs text-[#7c3aed] font-semibold hover:underline"
                    data-testid="btn-view-item-history"
                  >
                    Ver histórico ({itemDialog.item.versions.length} alteraç{itemDialog.item.versions.length === 1 ? "ão" : "ões"})
                  </button>
                )}
              </>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={openSaveToCatalog}
              disabled={catalogSaving || !itemDialog.item.description?.trim() || !Number(itemDialog.item.unit_value || 0)}
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 sm:mr-auto"
              data-testid="btn-save-to-catalog"
              title="Guardar este item no catálogo como base histórica"
            >
              <BookmarkPlus className="w-4 h-4 mr-1.5" /> Salvar no catálogo
            </Button>
            <Button variant="outline" onClick={() => setItemDialog({ open: false, mode: "add", item: EMPTY_ITEM })}>Cancelar</Button>
            <Button onClick={saveItem} disabled={savingItem} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="btn-save-item">{savingItem ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ServiceCatalogDialog
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        initialQuery={itemDialog.item.description || ""}
        onPick={pickFromCatalog}
      />

      {/* Dialog: Salvar no catálogo (define a faixa min/max) */}
      <Dialog
        open={saveToCatalogDialog.open}
        onOpenChange={(o) => !o && setSaveToCatalogDialog({ open: false, description: "", min_value: 0, max_value: 0, note: "" })}
      >
        <DialogContent className="max-w-md" data-testid="save-to-catalog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5 text-indigo-600" /> Salvar no catálogo
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Defina uma faixa de valor para este serviço. A escola verá o mínimo, máximo e média quando pesquisar.
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Descrição *</Label>
              <Textarea
                value={saveToCatalogDialog.description}
                onChange={(e) => setSaveToCatalogDialog((d) => ({ ...d, description: e.target.value }))}
                rows={2}
                data-testid="input-cat-desc"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-emerald-700">Valor mínimo (R$) *</Label>
                <MoneyInput
                  value={saveToCatalogDialog.min_value}
                  onChange={(v) => setSaveToCatalogDialog((d) => ({ ...d, min_value: v }))}
                  data-testid="input-cat-min"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-rose-700">Valor máximo (R$) *</Label>
                <MoneyInput
                  value={saveToCatalogDialog.max_value}
                  onChange={(v) => setSaveToCatalogDialog((d) => ({ ...d, max_value: v }))}
                  data-testid="input-cat-max"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">
              Dica: use a mesma unidade (ex.: por lâmpada R$ 15 a R$ 85, ou por metro quadrado de pintura R$ 25 a R$ 60).
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Observação (opcional)</Label>
              <Textarea
                value={saveToCatalogDialog.note}
                onChange={(e) => setSaveToCatalogDialog((d) => ({ ...d, note: e.target.value }))}
                rows={2}
                placeholder="Ex.: valor por unidade, inclui mão de obra, altura até 3m..."
                data-testid="input-cat-note"
              />
              <p className="text-[10px] text-slate-500">
                A escola vê essa observação ao consultar o preço.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveToCatalogDialog({ open: false, description: "", min_value: 0, max_value: 0, note: "" })}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmSaveToCatalog}
              disabled={catalogSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="btn-confirm-save-catalog"
            >
              {catalogSaving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Share dialog (after create or on item change) === */}
      {shareDialog.open && shareDialog.budget && shareDialog.school && (
        <ShareBudgetDialog
          open={shareDialog.open}
          onOpenChange={(o) => {
            if (!o) {
              const bid = shareDialog.budget.id;
              const wasNew = !id;
              setShareDialog({ open: false, budget: null, school: null, title: "Enviar para a escola?", headlineLabel: "Mensagem que será enviada", extraMessage: "" });
              if (wasNew) nav(`/orcamentos/${bid}/editar`);
            }
          }}
          budget={shareDialog.budget}
          school={shareDialog.school}
          title={shareDialog.title}
          headlineLabel={shareDialog.headlineLabel}
          extraMessage={shareDialog.extraMessage}
        />
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

const ItemPhotoField = ({ label, path, onUpload, onRemove, testid }) => {
  const [preview, setPreview] = React.useState(null);
  React.useEffect(() => {
    let obj;
    if (path) {
      api.get(`/files/${path}`, { responseType: "blob" }).then((r) => {
        obj = URL.createObjectURL(r.data);
        setPreview(obj);
      }).catch(() => {});
    } else {
      setPreview(null);
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="aspect-video rounded-lg border border-slate-200 bg-slate-50 overflow-hidden relative">
        {preview ? (
          <>
            <img src={preview} alt={label} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={onRemove}
              data-testid={`${testid}-remove`}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
            >
              ×
            </button>
          </>
        ) : (
          <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer text-xs text-slate-500 hover:bg-slate-100" data-testid={`${testid}-upload`}>
            + Enviar
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
          </label>
        )}
      </div>
    </div>
  );
};
